import { useCallback, useEffect, useRef, useState } from 'react';
import { ModalLayout, TextArea, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

/**
 * Reusable confirm modal for event decisions (Approve/Block, Promote/Reject,
 * Approve/Deny …). Shows a summary, an optional/required reason field, and a
 * single confirm button. Mock submit — the caller decides what onConfirm does.
 */
function DecisionConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  icon,
  breadcrumb,
  title,
  subtitle,
  summaryRows = [],
  requireReason = false,
  showReason = false,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  confirmVariant = 'primary',
  children,
}) {
  const { t } = useTranslation('work-queues-app');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // `submitting` state alone loses the double-click race — a second click can land
  // before React re-renders and fire a second POST. The ref blocks it synchronously,
  // the same guard the other Work Queue action modals use (EscalateModal/ReassignModal),
  // now that `onConfirm` performs a real HITL/governance resolve.
  const submittingRef = useRef(false);

  const canConfirm = !requireReason || reason.trim().length > 0;

  useEffect(() => {
    if (!isOpen) {
      setReason('');
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setReason('');
    onClose();
  }, [onClose, submitting]);

  const handleConfirm = useCallback(async () => {
    if (!canConfirm || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await Promise.resolve(onConfirm({ reason: reason.trim() }));
      setReason('');
      onClose();
    } catch {
      // Keep the modal open so the user can retry after an async failure.
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [canConfirm, onConfirm, reason, onClose]);

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      useBodyPortal
      titlePrefix={icon}
      breadcrumb={breadcrumb}
      title={title}
      subtitle={subtitle}
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        {summaryRows.length > 0 && (
          <dl className="flex flex-col gap-2 rounded-lg border border-edge bg-surface-muted px-4 py-3">
            {summaryRows.map((row) => (
              <div key={row.label} className="flex items-start justify-between gap-4 text-sm">
                <dt className="text-content-muted">{row.label}</dt>
                <dd className="text-right font-medium text-content">{row.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {children}

        {(requireReason || showReason) && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-content">
              {reasonLabel ?? t('confirm.reasonLabel')}
            </label>
            <TextArea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t border-edge px-6 py-4">
        <Button variant="secondary" size="sm" onClick={handleClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant={confirmVariant}
          size="sm"
          disabled={!canConfirm || submitting}
          onClick={handleConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </ModalLayout>
  );
}

export default DecisionConfirmModal;
